import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getWorkspaceTheme } from '../../../components/Atendimento/Workspace/workspaceTheme';
import { helpdeskRoutes } from '../../../routes/helpdeskRoutes';
import { WorkspaceQueue } from '../../../components/Atendimento/Workspace/WorkspaceQueue';
import { WorkspaceChat } from '../../../components/Atendimento/Workspace/WorkspaceChat';
import { WorkspaceContactPanel } from '../../../components/Atendimento/Workspace/WorkspaceContactPanel';
import { useWorkspaceConversations } from '../../../components/Atendimento/Workspace/useWorkspaceConversations';
import { SupportNotificationBadge } from '../../../components/Atendimento/Workspace/SupportNotificationBadge';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

export const WorkspaceConsolePage: React.FC = () => {
  const theme = useMemo(() => getWorkspaceTheme('light'), []);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedParam = searchParams.get('selected');
  const workspace = useWorkspaceConversations({ initialSelectedId: selectedParam });
  const [contactPanelOpen, setContactPanelOpen] = useState(false);
  const closeContactPanel = useCallback(() => {
    setContactPanelOpen(false);
  }, []);

  useEffect(() => {
    if (location.pathname !== helpdeskRoutes.workspace)
    {
      return;
    }

    if (!workspace.selectedId)
    {
      if (!selectedParam)
      {
        return;
      }

      navigate(helpdeskRoutes.workspace, { replace: true });
      return;
    }

    if (selectedParam === workspace.selectedId)
    {
      return;
    }

    navigate(helpdeskRoutes.workspaceConversation(workspace.selectedId), { replace: true });
  }, [workspace.selectedId, selectedParam, navigate, location.pathname]);

  useEffect(() => {
    setContactPanelOpen(false);
  }, [workspace.selectedId]);

  useEffect(() => {
    if (!contactPanelOpen)
    {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape')
      {
        return;
      }

      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable))
      {
        return;
      }

      closeContactPanel();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [contactPanelOpen, closeContactPanel]);

  const selectedInboxId =
    workspace.inboxFilter.kind === 'inbox' ? workspace.inboxFilter.inboxId : undefined;

  return (
    <div className={`h-full min-h-0 flex flex-col overflow-hidden ${theme.shell}`}>
      <div className={`flex-shrink-0 px-3 py-2 lg:px-4 lg:py-3 border-b ${theme.surface} ${theme.border}`}>
        <div className="flex items-center gap-2 lg:gap-3">
          <div className="min-w-0 hidden sm:block">
            <p className={`text-xs font-semibold uppercase tracking-wide ${theme.faint}`}>
              Atendimento
            </p>
            <p className={`text-sm font-semibold ${theme.text}`}>
              Fila, conversa e contexto do contato
            </p>
          </div>

          <label
            className={`flex flex-1 max-w-xl items-center gap-2 rounded-lg border px-3 py-2 text-sm ${theme.topbarSearch} focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500`}
          >
            <MagnifyingGlassIcon className="h-5 w-5 flex-shrink-0" />
            <input
              type="text"
              value={workspace.searchInput}
              onChange={(event) => workspace.setSearchInput(event.target.value)}
              placeholder="Buscar conversas…"
              className="flex-1 min-w-0 bg-transparent border-0 p-0 text-base lg:text-sm focus:ring-0 focus:outline-none"
              aria-label="Buscar conversas"
            />
            {workspace.searchInput ? (
              <button
                type="button"
                onClick={() => workspace.setSearchInput('')}
                className={`p-0.5 rounded ${theme.muted}`}
                title="Limpar busca"
                aria-label="Limpar busca"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            ) : (
              <kbd className={`hidden sm:inline text-[10px] px-1.5 py-0.5 rounded border ${theme.border} ${theme.surface}`}>
                Ctrl K
              </kbd>
            )}
          </label>

          <SupportNotificationBadge
            theme={theme}
            inboxId={selectedInboxId}
            onNavigate={(conversationId) => workspace.selectConversation(conversationId)}
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 flex overflow-hidden p-0 lg:p-3 lg:gap-3">
        <div className={`relative flex min-w-0 flex-1 overflow-hidden lg:rounded-lg lg:border ${theme.border} ${theme.surface}`}>
          <WorkspaceQueue
            theme={theme}
            conversations={workspace.conversations}
            selectedId={workspace.selectedId}
            loading={workspace.listLoading}
            accessibleInboxes={workspace.accessibleInboxes}
            inboxFilter={workspace.inboxFilter}
            queueScope={workspace.queueScope}
            queueCounts={workspace.queueCounts}
            activeView={workspace.activeView}
            onSelect={workspace.selectConversation}
            onInboxFilterChange={workspace.setInboxFilter}
            onQueueScopeChange={workspace.setQueueScope}
            onViewChange={workspace.setActiveView}
          />
          <div className={`min-h-0 min-w-0 flex-1 flex-col ${workspace.selectedId ? 'flex' : 'hidden lg:flex'}`}>
          <WorkspaceChat
            theme={theme}
            conversation={workspace.selected}
            messages={workspace.messages}
            groups={workspace.groups}
            selectedGroupId={workspace.selectedGroupId}
            loading={workspace.detailLoading}
            draft={workspace.draft}
            sending={workspace.sending}
            actionLoading={workspace.actionLoading}
            conductor={workspace.conductor}
            currentUserId={workspace.currentUserId}
            canAssume={workspace.canAssume}
            canHandoffToHuman={workspace.canHandoffToHuman}
            canHandoffToBot={workspace.canHandoffToBot}
            canCapture={workspace.canCapture}
            canReopen={workspace.canReopen}
            conversationLabels={workspace.conversationLabels}
            onDraftChange={workspace.setDraft}
            onSend={workspace.sendMessage}
            onRetryMessage={workspace.retryMessage}
            retryingMessageId={workspace.retryingMessageId}
            onSelectedGroupChange={workspace.setSelectedGroupId}
            onTransferGroup={workspace.transferGroup}
            onAssume={workspace.assumeConversation}
            onHandoffToHuman={workspace.handoffToHuman}
            onHandoffToBot={workspace.handoffToBot}
            onUpdateStatus={workspace.updateStatus}
            onResolve={workspace.resolveAndNext}
            onReopen={workspace.reopenConversation}
            onDelete={workspace.deleteConversation}
            onMarkRead={workspace.markAsRead}
            onMarkUnread={workspace.markAsUnread}
            onTranscriptVisible={workspace.acknowledgeTranscriptVisible}
            onUserReadIntent={workspace.acknowledgeActiveRead}
            onSelectConversation={workspace.selectConversation}
            contactPanelOpen={contactPanelOpen}
            onToggleContactPanel={() => setContactPanelOpen((open) => !open)}
            onBackToQueue={workspace.clearSelection}
          />
          </div>
          <WorkspaceContactPanel
            theme={theme}
            conversation={workspace.selected}
            isOpen={contactPanelOpen}
            conversationLabels={workspace.conversationLabels}
            unappliedLabels={workspace.unappliedLabels}
            selectedLabelId={workspace.selectedLabelId}
            labelUpdating={workspace.labelUpdating}
            onClose={closeContactPanel}
            onSelectedLabelChange={workspace.setSelectedLabelId}
            onApplyLabel={() => void workspace.applyLabel()}
            onRemoveLabel={(labelId) => void workspace.removeLabel(labelId)}
          />
        </div>
      </div>
    </div>
  );
};
